/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import { useCallback, useEffect } from 'react';
/* eslint camelcase: 0 */
import URI from 'urijs';
import { buildQueryContext, ensureIsArray, getChartBuildQueryRegistry, getChartMetadataRegistry, SupersetClient } from '@superset-ui/core';
import { availableDomains } from 'src/utils/hostNamesConfig';
import { safeStringify } from 'src/utils/safeStringify';
import { optionLabel } from 'src/utils/common';
import { URL_PARAMS } from 'src/constants';
import { MULTI_OPERATORS, OPERATOR_ENUM_TO_OPERATOR_TYPE, UNSAVED_CHART_ID } from 'src/explore/constants';
import { DashboardStandaloneMode } from 'src/dashboard/util/constants';
import { func } from 'prop-types';

export function getChartKey(explore) {
	const { slice, form_data } = explore;
	return slice?.slice_id ?? form_data?.slice_id ?? UNSAVED_CHART_ID;
}

let requestCounter = 0;
export function getHostName(allowDomainSharding = false) {
	let currentIndex = 0;
	if (allowDomainSharding) {
		currentIndex = requestCounter % availableDomains.length;
		requestCounter += 1;

		// if domain sharding is enabled, skip main domain for fetching chart API
		// leave main domain free for other calls like fav star, save change, etc.
		// to make dashboard be responsive when it's loading large number of charts
		if (currentIndex === 0) {
			currentIndex += 1;
			requestCounter += 1;
		}
	}
	return availableDomains[currentIndex];
}

export function getAnnotationJsonUrl(slice_id, force) {
	if (slice_id === null || slice_id === undefined) {
		return null;
	}

	const uri = URI(window.location.search);
	return uri
		.pathname('/api/v1/chart/data')
		.search({
			form_data: safeStringify({ slice_id }),
			force,
		})
		.toString();
}

export function getURIDirectory(endpointType = 'base') {
	// Building the directory part of the URI
	if (['full', 'json', 'csv', 'query', 'results', 'samples'].includes(endpointType)) {
		return '/superset/explore_json/';
	}
	return '/explore/';
}

export function mountExploreUrl(endpointType, extraSearch = {}, force = false) {
	const uri = new URI('/');
	const directory = getURIDirectory(endpointType);
	const search = uri.search(true);
	Object.keys(extraSearch).forEach((key) => {
		search[key] = extraSearch[key];
	});
	if (endpointType === URL_PARAMS.standalone.name) {
		if (force) {
			search.force = '1';
		}
		search.standalone = DashboardStandaloneMode.HIDE_NAV;
	}
	return uri.directory(directory).search(search).toString();
}

export function getChartDataUri({ path, qs, allowDomainSharding = false }) {
	// The search params from the window.location are carried through,
	// but can be specified with curUrl (used for unit tests to spoof
	// the window.location).
	let uri = new URI({
		protocol: window.location.protocol.slice(0, -1),
		hostname: getHostName(allowDomainSharding),
		port: window.location.port ? window.location.port : '',
		path,
	});
	if (qs) {
		uri = uri.search(qs);
	}
	return uri;
}

/**
 * This gets the minimal url for the given form data.
 * If there are dashboard overrides present in the form data,
 * they will not be included in the url.
 */
export function getExploreUrl({ formData, endpointType = 'base', force = false, curUrl = null, requestParams = {}, allowDomainSharding = false, method = 'POST' }) {
	if (!formData.datasource) {
		return null;
	}

	// label_colors should not pollute the URL
	// eslint-disable-next-line no-param-reassign
	delete formData.label_colors;

	let uri = getChartDataUri({ path: '/', allowDomainSharding });
	if (curUrl) {
		uri = URI(URI(curUrl).search());
	}

	const directory = getURIDirectory(endpointType);

	// Building the querystring (search) part of the URI
	const search = uri.search(true);
	const { slice_id, extra_filters, adhoc_filters, viz_type } = formData;
	if (slice_id) {
		const form_data = { slice_id };
		if (method === 'GET') {
			form_data.viz_type = viz_type;
			if (extra_filters && extra_filters.length) {
				form_data.extra_filters = extra_filters;
			}
			if (adhoc_filters && adhoc_filters.length) {
				form_data.adhoc_filters = adhoc_filters;
			}
		}
		search.form_data = safeStringify(form_data);
	}
	if (force) {
		search.force = 'true';
	}
	if (endpointType === 'csv') {
		search.csv = 'true';
	}
	if (endpointType === URL_PARAMS.standalone.name) {
		search.standalone = '1';
	}
	if (endpointType === 'query') {
		search.query = 'true';
	}
	if (endpointType === 'results') {
		search.results = 'true';
	}
	if (endpointType === 'samples') {
		search.samples = 'true';
	}
	const paramNames = Object.keys(requestParams);
	if (paramNames.length) {
		paramNames.forEach((name) => {
			if (requestParams.hasOwnProperty(name)) {
				search[name] = requestParams[name];
			}
		});
	}
	return uri.search(search).directory(directory).toString();
}

export const getQuerySettings = (formData) => {
	const vizMetadata = getChartMetadataRegistry().get(formData.viz_type);
	return [vizMetadata?.useLegacyApi ?? false, vizMetadata?.parseMethod ?? 'json-bigint'];
};

export const buildV1ChartDataPayload = ({ formData, force, resultFormat, resultType, setDataMask, ownState }) => {
	//#region Hàm init data cho filter số liệu doanh thu, số hóa đơn,...
	function formatDate(date) {
		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const day = String(date.getDate()).padStart(2, '0');
		const formattedDate = `${year}-${month}-${day}`;
		return formattedDate;
	}
	//Khi khởi tạo sẽ không có trường startDate --> hàm này để tính toán startDate với tham số là currentDate
	function calculateStartDate(numberOfDays, endDate) {
		const endDateObject = new Date(endDate);
		if (isNaN(endDateObject.getTime())) {
			return null; // Ngày kết thúc không hợp lệ, trả về null
		}
		const startDateObject = new Date(endDateObject);
		startDateObject.setDate(endDateObject.getDate() - numberOfDays);
		return startDateObject.toISOString().split('T')[0];
	}

	function changeMetric(index, metric, metric_b) {
		metric[index].label = 'SUM(' + sessionStorage.getItem('changeValue') + ')';
		metric[index].column['column_name'] = sessionStorage.getItem('changeValue');
		if (formData.viz_type === 'mixed_timeseries_mobile' || formData.viz_type === 'mixed_timeseries_web') {
			if(listColumnLanguageMapping[sessionStorage.getItem('changeValue')]){
				metric_b[index].label = listColumnLanguageMapping[sessionStorage.getItem('changeValue')]+ ' - comparison period';
			}
			metric_b[index].column['column_name'] = sessionStorage.getItem('changeValue');
		}
	}

	//KR2-48589: Khi chọn bộ lọc doanh thu không nhận dữ liệu
	function changeMetricBySQL(index, metric, metric_b) {
		metric[index].label = 'SUM(' + sessionStorage.getItem('changeValue') + ')';
		metric[index].sqlExpression = 'SUM(' + sessionStorage.getItem('changeValue') + ')';
		if (formData.viz_type === 'mixed_timeseries_mobile' || formData.viz_type === 'mixed_timeseries_web') {
			if(listColumnLanguageMapping[sessionStorage.getItem('changeValue')]){
				metric_b[index].label = listColumnLanguageMapping[sessionStorage.getItem('changeValue')]+ ' - comparison period';
			}
			metric_b[index].sqlExpression = 'SUM(' + sessionStorage.getItem('changeValue') + ')';
		}
	}

	function changeMetricFirst(index, metric, metric_b) {
		sessionStorage.setItem('changeValue', 'sale_revenue');
		metric[index].label = 'Sale revenue';
		metric[index].column['column_name'] = 'sale_revenue';
		if (formData.viz_type === 'mixed_timeseries_mobile' || formData.viz_type === 'mixed_timeseries_web') {
			metric_b[index].label = 'Sale revenue - comparison period';
			metric_b[index].column['column_name'] = 'sale_revenue';
		}
	}
	function changeMetricBySQLFirst(index, metric, metric_b) {
		sessionStorage.setItem('changeValue', 'sale_revenue');
		metric[index].label = 'Sale revenue';
		metric[index].sqlExpression = 'SUM(sale_revenue)';
		if (formData.viz_type === 'mixed_timeseries_mobile' || formData.viz_type === 'mixed_timeseries_web') {
			metric_b[index].label = 'Sale revenue - comparison period';
			metric_b[index].sqlExpression = 'SUM(sale_revenue)';
		}
	}

	function calcPreviousDate() {
		const startDatePeriod = calculateStartDate(sessionStorage.getItem('totalDays'), sessionStorage.getItem('startDate'));

		if (formData.url_params === undefined) {
			formData.url_params = {};
		}
		if (sessionStorage.getItem('startDate') === null || sessionStorage.getItem('toDate') === null) {
			sessionStorage.setItem('toDate', formatDate(new Date()));
			sessionStorage.setItem('startDate', calculateStartDate(30, sessionStorage.getItem('toDate')));
		}

		let newEndDate = calculateStartDate(1, sessionStorage.getItem('startDate'));
		if(!formData.is_stack_chart){
			formData.url_params['ky1'] = sessionStorage.getItem('startDate') ?? '';
			formData.url_params['ky2'] = sessionStorage.getItem('toDate').replace('T', ' ') ?? '';
		}else{
			formData.url_params['ky1Virtual'] = sessionStorage.getItem('startDate') ?? '';
			formData.url_params['ky2Virtual'] = sessionStorage.getItem('toDate').replace('T', ' ') ?? '';
		}
		

		if (formData.extra_form_data['time_column']?.includes('Previous month')) {
			function getPreviousMonthDate(currentDate, isStartDate, totalMonth) {
				const currentDateObj = new Date(currentDate);
				currentDateObj.setHours(0, 0, 0, 0);
				const currentMonth = currentDateObj.getMonth();
				const currentDay = currentDateObj.getDate();
				const lastDay = new Date(currentDateObj.getFullYear(), currentDateObj.getMonth() + 1, 0).getDate();

				currentDateObj.setMonth(currentDateObj.getMonth() - 1);
				const lastDayPrevious = new Date(currentDateObj.getFullYear(), currentDateObj.getMonth() + 1, 0).getDate();
				//Nếu là startDate và ngày hiện tại = ngày cuối cùng thì lấy ngày cuối cùng của tháng trước
				//Sử dụng cho TH ngày hiện tại ngày 30 mà tháng trước có 28, 29 ngày (currentDay === 30 && currentMonth === 2)
				if ((lastDay === currentDay || currentDay === 30 || currentDay === 29) && currentMonth === 2) {
					currentDateObj.setDate(0);
				}
				//Nếu là toDate và ngày hiện tại là ngày cuối cùng của tháng và lớn hơn ngày cuối cùng của tháng trước ví dụ tháng 12 có 31 ngày, tháng 11 có 30 ngày
				if (currentDay === 31 && (currentMonth !== 2 && currentMonth !== 0)) {
					currentDateObj.setDate(0);
				}
				//Nếu là toDate và ngày hiện tại bằng 1 và ngày của tháng trước bằng 30 --> fix lỗi kỳ hiện tại 30, kỳ sau là 31
				if (!isStartDate && currentDay === 1 && lastDayPrevious !== 31) {
					currentDateObj.setDate(currentDateObj.getDate() - 1);
				}
				
				const previousYear = currentDateObj.getFullYear();
				const previousMonth = currentDateObj.getMonth() + 1; // Lưu ý: Tháng bắt đầu từ 0
				let previousDay = currentDateObj.getDate();
				//Fix cho TH tháng 2 và kỳ trước là tháng 1 và toDate: 1 cho nam nhuan, 30 nam k nhuan
				if (currentMonth === 2 && currentDay === 1 && !isStartDate) {
					const isLeapYear = (previousYear % 4 === 0 && previousYear % 100 !== 0) || previousYear % 400 === 0;
					previousDay = isLeapYear ? 30 : 29;
				}
				const previousMonthDate = `${previousYear}-${String(previousMonth).padStart(2, '0')}-${String(previousDay).padStart(2, '0')}`;
				return previousMonthDate;
			}
			function getMonth(startDate, endDate){
				const startDateObj = new Date(startDate);
				const endDateObj = new Date(endDate);
				const currentMonthStartDate = startDateObj.getMonth();
				const currentMonthEndDate = endDateObj.getMonth();
				return currentMonthEndDate - currentMonthStartDate + 1;
			}

			const totalMonth = getMonth(sessionStorage.getItem('startDate'), sessionStorage.getItem('toDate'))
			const startDatePeriodMonth = getPreviousMonthDate(sessionStorage.getItem('startDate'), true, totalMonth);
			let endDatePeriodMonth = getPreviousMonthDate(sessionStorage.getItem('toDate'), false, totalMonth);
			endDatePeriodMonth = endDatePeriodMonth + ' 23:59:59';
				if(!formData.is_stack_chart){
						formData.url_params['ky3'] = startDatePeriodMonth;
						formData.url_params['ky4'] = endDatePeriodMonth;
				}else{
					formData.url_params['ky3Virtual'] = startDatePeriodMonth;
					formData.url_params['ky4Virtual'] = endDatePeriodMonth;
				}
		} else if (formData.extra_form_data['time_column']?.includes('Previous year')) {
			function getPreviousYearDate(currentDate, isStartDate) {
				const currentDateObj = new Date(currentDate);
				const currentDay = currentDateObj.getDate();
				const currentMonth = currentDateObj.getMonth();
				currentDateObj.setFullYear(currentDateObj.getFullYear() - 1);
				const previousYear = currentDateObj.getFullYear();
				let previousDay = currentDateObj.getDate();
				let previousMonth = currentDateObj.getMonth() + 1; // Lưu ý: Tháng bắt đầu từ 0
				// if (currentMonth === 2 && !isStartDate && currentDay === 1) {
				const isLeapYear = (previousYear % 4 === 0 && previousYear % 100 !== 0) || previousYear % 400 === 0;

				if (currentMonth === 1) {
					const isLeapYear = (previousYear % 4 === 0 && previousYear % 100 !== 0) || previousYear % 400 === 0;
					if(currentDay === 29 && !isLeapYear){
						previousDay = 28;
						previousMonth = previousMonth - 1;
					}else if(currentDay === 28 && isLeapYear){
						previousDay = 29;
					}
				}
				const previousDate = `${previousYear}-${String(previousMonth).padStart(2, '0')}-${String(previousDay).padStart(2, '0')}`;
				return previousDate;
			}
			const startDatePeriodYear = getPreviousYearDate(sessionStorage.getItem('startDate'), true);
			let endDatePeriodYear = getPreviousYearDate(sessionStorage.getItem('toDate'), false);
			if(!formData.is_stack_chart){
				formData.url_params['ky3'] = startDatePeriodYear;
				endDatePeriodYear = endDatePeriodYear + ' 23:59:59';
				formData.url_params['ky4'] = endDatePeriodYear;
			}else{
				formData.url_params['ky3Virtual'] = startDatePeriodYear;
				endDatePeriodYear = endDatePeriodYear + ' 23:59:59';
				formData.url_params['ky4Virtual'] = endDatePeriodYear;
			}
		} else {
			if(!formData.is_stack_chart){
				newEndDate = newEndDate + ' 23:59:59';
				formData.url_params['ky3'] = startDatePeriod ?? '';
				formData.url_params['ky4'] = newEndDate ?? '';
			}else{
				newEndDate = newEndDate + ' 23:59:59';
				formData.url_params['ky3Virtual'] = startDatePeriod ?? '';
				formData.url_params['ky4Virtual'] = newEndDate ?? '';
			}
		}
	}

	if (formData.extra_form_data.filters !== undefined) {
		formData.extra_form_data.filters.map((value, index) => {
			if (value['val'][0] === 'values') {
				formData.extra_form_data.filters.splice(index, 1);
			}
		});
	}

		if (formData.metrics && !formData.is_stack_chart && formData.is_filter_revenue) {
			formData.metrics.map((metric, index) => {
				//Khởi tạo giá trị
				if (sessionStorage.getItem('changeValue') === null) {
					sessionStorage.setItem('changeValue', metric.column ? metric.column['column_name'] : 'sale_revenue');
					
				}
				if(sessionStorage.getItem('startDate') === null && sessionStorage.getItem('toDate') === null){
					sessionStorage.setItem('toDate', formatDate(new Date()) + 'T23:59:59');
					sessionStorage.setItem('startDate', calculateStartDate(31, sessionStorage.getItem('toDate')));
				}

				if (formData.metrics !== undefined) {
					if(sessionStorage.getItem('callFirst') && sessionStorage.getItem('callFirst') === "true"){
						if (formData.metrics[index].column !== undefined && formData.metrics[index].column !== null) {
							changeMetricFirst(index, formData.metrics, formData.metrics_b);
						} else {
							changeMetricBySQLFirst(index, formData.metrics, formData.metrics_b);
						}
					}
					else{
						if (formData.metrics[index].column !== undefined && formData.metrics[index].column !== null) {
							changeMetric(index, formData.metrics, formData.metrics_b);
						} else {
							changeMetricBySQL(index, formData.metrics, formData.metrics_b);
						}
					}
				}
			});
		} else if (!formData.is_stack_chart && formData.is_filter_revenue) {
			if (sessionStorage.getItem('changeValue') === null) {
				sessionStorage.setItem('changeValue', 'sale_revenue');
				
			}
			if(sessionStorage.getItem('startDate') === null && sessionStorage.getItem('toDate') === null){
				sessionStorage.setItem('toDate', formatDate(new Date()));
				sessionStorage.setItem('startDate', calculateStartDate(30, sessionStorage.getItem('toDate')));
			}
			//#endregion

			//#region Sử dụng cho trường hợp doanh thu, số hóa đơn, doanh thu thuần, lợi nhuận gộp
			if (formData.extra_form_data.filters !== undefined) {
				formData.extra_form_data.filters.map((value, index) => {
					if (value['val'][0] === 'values') {
						formData.extra_form_data.filters.splice(index, 1);
					}
				});
			}
			if (formData.metrics !== undefined) {
				if(sessionStorage.getItem('callFirst') && sessionStorage.getItem('callFirst') === "true"){
					if (formData.metrics[0].column !== undefined && formData.metrics[0].column !== null) {
						changeMetricFirst(index, formData.metrics, formData.metrics_b);
					} 
				}
				else{
					if(formData.metrics[0].column !== undefined && formData.metrics[0].column !== null){
						changeMetric(index, formData.metrics, formData.metrics_b);
					}
				}
			}
		}


	//#endregion

	//#region Sử dụng thay thế cho bar chart. Theo điều kiện filter select time
	//Mix chart sẽ tính toán các ngày của kỳ để truyền qua url param
	if ((formData.viz_type === 'mixed_timeseries_mobile' || formData.viz_type === 'mixed_timeseries_web')) {
		calcPreviousDate();

		//Stack chart: Trường hợp này thì truyền thêm param previous_date Sử dụng url param
		if (formData.is_stack_chart) {
			formData.url_params['previous_date'] = formData.url_params['ky3Virtual'] + ' : ' + formData.url_params['ky4Virtual'];
		}
	}

	//KR2-48989: Với bignumber, có tùy chọn sử dụng query union để dùng cho compare
	if (formData.is_compare_manual && (formData.viz_type === 'big_number_mobile' || formData.viz_type === 'big_number_web')) {
		calcPreviousDate();
		formData.url_params['previous_date'] = formData.url_params['ky3'] + ' : ' + formData.url_params['ky4'];
	}

	//#endregion
	const buildQuery =
		getChartBuildQueryRegistry().get(formData.viz_type) ??
		((buildQueryformData) =>
			buildQueryContext(buildQueryformData, (baseQueryObject) => [
				{
					...baseQueryObject,
				},
			]));

	return buildQuery(
		{
			...formData,
			force,
			result_format: resultFormat,
			result_type: resultType,
		},
		{
			ownState,
			hooks: {
				setDataMask,
			},
		}
	);
};

export const getLegacyEndpointType = ({ resultType, resultFormat }) => (resultFormat === 'csv' ? resultFormat : resultType);

export const exportChart = ({ formData, resultFormat = 'json', resultType = 'full', force = false, ownState = {} }) => {
	let url;
	let payload;
	const [useLegacyApi, parseMethod] = getQuerySettings(formData);
	if (useLegacyApi) {
		const endpointType = getLegacyEndpointType({ resultFormat, resultType });
		url = getExploreUrl({
			formData,
			endpointType,
			allowDomainSharding: false,
		});
		payload = formData;
	} else {
		url = '/api/v1/chart/data';
		payload = buildV1ChartDataPayload({
			formData,
			force,
			resultFormat,
			resultType,
			ownState,
			parseMethod,
		});
	}

	SupersetClient.postForm(url, { form_data: safeStringify(payload) });
};

export const exploreChart = (formData, requestParams) => {
	const url = getExploreUrl({
		formData,
		endpointType: 'base',
		allowDomainSharding: false,
		requestParams,
	});
	SupersetClient.postForm(url, { form_data: safeStringify(formData) });
};

export const useDebouncedEffect = (effect, delay, deps) => {
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const callback = useCallback(effect, deps);

	useEffect(() => {
		const handler = setTimeout(() => {
			callback();
		}, delay);

		return () => {
			clearTimeout(handler);
		};
	}, [callback, delay]);
};

export const getSimpleSQLExpression = (subject, operator, comparator) => {
	const isMulti = [...MULTI_OPERATORS].map((op) => OPERATOR_ENUM_TO_OPERATOR_TYPE[op].operation).indexOf(operator) >= 0;
	// If returned value is an object after changing dataset
	let expression = typeof subject === 'object' ? subject?.column_name ?? '' : subject ?? '';
	if (subject && operator) {
		expression += ` ${operator}`;
		const firstValue = isMulti && Array.isArray(comparator) ? comparator[0] : comparator;
		const comparatorArray = ensureIsArray(comparator);
		const isString = firstValue !== undefined && Number.isNaN(Number(firstValue));
		const quote = isString ? "'" : '';
		const [prefix, suffix] = isMulti ? ['(', ')'] : ['', ''];
		const formattedComparators = comparatorArray.map((val) => optionLabel(val)).map((val) => `${quote}${isString ? String(val).replace("'", "''") : val}${quote}`);
		if (comparatorArray.length > 0) {
			expression += ` ${prefix}${formattedComparators.join(', ')}${suffix}`;
		}
	}
	return expression;
};

export function formatSelectOptions(options) {
	return options.map((opt) => [opt, opt.toString()]);
}
